import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, ChevronDown, ChevronUp, Check } from "lucide-react";
import { toast } from "sonner";
import { fireConfetti } from "@/lib/celebrate";

const TIPS_BY_KEY = {
  avatar: { label: "Photo de profil", action: "Modifier mon profil", to: "edit" },
  cover: { label: "Photo de couverture", action: "Modifier ma cover", to: "edit" },
  bio: { label: "Biographie", action: "Ajouter une bio", to: "edit" },
  city: { label: "Ville", action: "Ajouter ma ville", to: "edit" },
  genres: { label: "Genres musicaux", action: "Choisir mes genres", to: "edit" },
  social: { label: "Reseaux sociaux", action: "Ajouter mes reseaux", to: "edit" },
  email_verified: { label: "Email verifie", action: "Verifier mon email", to: "/settings" },
  first_follow: { label: "Suivre un artiste", action: "Decouvrir des artistes", to: "/discover" },
  first_post: { label: "Publier un post", action: "Aller au dashboard", to: "/dashboard" },
};

export const ProfileCompletionBar = forwardRef(({ onEdit }, ref) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchScore = async (silent = false) => {
    try {
      const r = await api.get("/users/me/profile-completion");
      const newScore = r.data.score;
      if (!silent && data && newScore > data.score) {
        const delta = newScore - data.score;
        toast.success(`+${delta} pts ! Profil rempli a ${newScore}%`, { duration: 4000 });
        if (newScore === 100) {
          fireConfetti("medium");
          toast.success("Profil complet a 100% ! Bravo !", { duration: 5000 });
        }
      }
      setData(r.data);
    } catch {}
  };

  useEffect(() => {
    fetchScore(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    refresh: () => fetchScore(false),
  }));

  if (!data) return null;
  if (data.score === 100) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-emerald-500/15 to-[#7000FF]/15 border border-emerald-500/30 rounded-3xl p-4 flex items-center gap-3 shadow-sm"
        data-testid="profile-completion-100"
      >
        <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 flex items-center justify-center shrink-0">
          <Check className="w-5 h-5 text-emerald-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-sm text-card-foreground">Profil complet a 100% !</p>
          <p className="text-[11px] text-muted-foreground">Tu es au top, partage tes liens et tes coups de coeur</p>
        </div>
      </motion.div>
    );
  }

  const score = data.score;
  const missing = data.missing || [];
  const color =
    score >= 80 ? "from-emerald-500 to-emerald-400" :
    score >= 50 ? "from-[#FF6B35] to-[#FF8FA3]" :
    "from-rose-500 to-pink-400";

  const handleAction = (key) => {
    const tip = TIPS_BY_KEY[key];
    if (!tip) return;
    if (tip.to === "edit") {
      onEdit?.();
    } else {
      navigate(tip.to);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-3xl p-4 shadow-sm"
      data-testid="profile-completion-bar"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 text-left"
        data-testid="profile-completion-toggle"
      >
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="font-heading font-bold text-sm text-card-foreground">
              Profil rempli a {score}%
            </p>
            <span className="text-[10px] text-muted-foreground font-mono-credits">
              {missing.length} a faire
            </span>
          </div>
          <div className="mt-1.5 bg-muted rounded-full h-2 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${score}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className={`h-full bg-gradient-to-r ${color} rounded-full`}
            />
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-3 pt-3 border-t border-border space-y-2 overflow-hidden"
          >
            {missing.map((m) => {
              const tip = TIPS_BY_KEY[m.key] || {};
              return (
                <button
                  key={m.key}
                  onClick={() => handleAction(m.key)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted text-left transition-colors group"
                  data-testid={`completion-action-${m.key}`}
                >
                  <span className="text-[10px] font-mono-credits font-bold text-[#FF6B35] bg-[#FF6B35]/10 px-2 py-1 rounded-lg shrink-0">
                    +{m.points}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-card-foreground truncate">
                      {m.label}
                    </p>
                    {tip.action && (
                      <p className="text-[10px] text-[#FF6B35] group-hover:underline">
                        {tip.action} &rarr;
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

ProfileCompletionBar.displayName = "ProfileCompletionBar";
